import React, { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar, Clock, User, DollarSign, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { cancelBooking, rescheduleBooking } from "@/services/bookingActions";
import DateTimeSelectionStep from "@/components/booking-shared/DateTimeSelectionStep";

interface Appointment {
  id: string;
  customerName: string;
  service: string;
  date: Date;
  time: string;
  duration: number;
  status: "confirmed" | "completed" | "cancelled";
  grossAmount: number;
  paymentStatus: "paid" | "pending";
  serviceId?: string;
  providerId?: string;
}

interface AppointmentDrawerProps {
  appointment: Appointment | null;
  onClose: () => void;
  onActionComplete: () => void;
  businessId: string;
}

type DrawerView = "view" | "cancel" | "reschedule";

export function AppointmentDrawer({
  appointment,
  onClose,
  onActionComplete,
  businessId,
}: AppointmentDrawerProps) {
  const [view, setView] = useState<DrawerView>("view");
  const [cancelReason, setCancelReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [newDateTime, setNewDateTime] = useState<{ date: Date; time: string } | null>(null);

  const handleClose = () => {
    setView("view");
    setCancelReason("");
    setNewDateTime(null);
    onClose();
  };

  const handleCancel = async () => {
    if (!appointment) return;
    setIsLoading(true);
    try {
      const result = await cancelBooking({
        bookingId: appointment.id,
        reason: cancelReason.trim() || undefined,
        mode: "admin",
      });
      if (!result.success) throw new Error(result.error);
      toast.success("Appointment cancelled");
      handleClose();
      onActionComplete();
    } catch (e: any) {
      toast.error(e?.message || "Failed to cancel appointment");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReschedule = async () => {
    if (!appointment || !newDateTime) return;
    setIsLoading(true);
    try {
      // Convert 12h time to 24h UTC datetime
      const [timePart, period] = newDateTime.time.split(" ");
      const [hours, minutes] = timePart.split(":").map(Number);
      let h24 = hours;
      if (period === "PM" && hours !== 12) h24 += 12;
      if (period === "AM" && hours === 12) h24 = 0;

      const year = newDateTime.date.getFullYear();
      const month = newDateTime.date.getMonth();
      const day = newDateTime.date.getDate();
      const utcDatetime = new Date(Date.UTC(year, month, day, h24, minutes, 0)).toISOString();

      const result = await rescheduleBooking({
        bookingId: appointment.id,
        newDatetime: utcDatetime,
        mode: "admin",
      });
      if (!result.success) throw new Error(result.error);
      if (result.calendarWarning) {
        toast.warning(result.calendarWarning);
      } else {
        toast.success("Appointment rescheduled");
      }
      handleClose();
      onActionComplete();
    } catch (e: any) {
      toast.error(e?.message || "Failed to reschedule appointment");
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(amount);

  const isActionable = appointment?.status === "confirmed";

  return (
    <Sheet open={!!appointment} onOpenChange={(open) => !open && handleClose()}>
      <SheetContent className="sm:max-w-[400px] w-full overflow-y-auto">
        {appointment && (
          <>
            {view === "view" && (
              <>
                <SheetHeader>
                  <SheetTitle>Appointment Details</SheetTitle>
                  <SheetDescription>
                    {appointment.status === "confirmed"
                      ? "Upcoming appointment"
                      : appointment.status === "completed"
                        ? "Completed"
                        : "Cancelled"}
                  </SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="font-medium">{appointment.customerName}</p>
                      <p className="text-sm text-muted-foreground">{appointment.service}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-muted-foreground" />
                    <p className="text-sm">
                      {appointment.date.toLocaleDateString("en-US", {
                        weekday: "long",
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-muted-foreground" />
                    <p className="text-sm">
                      {appointment.time} · {appointment.duration} min
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <DollarSign className="h-5 w-5 text-muted-foreground" />
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">
                        {formatCurrency(appointment.grossAmount)}
                      </p>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          appointment.paymentStatus === "paid"
                            ? "bg-green-100 text-green-700"
                            : "bg-yellow-100 text-yellow-700"
                        }`}
                      >
                        {appointment.paymentStatus === "paid" ? "Paid" : "Unpaid"}
                      </span>
                    </div>
                  </div>
                </div>

                {isActionable && (
                  <div className="mt-8 flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setView("reschedule")}
                    >
                      Reschedule
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1 text-destructive"
                      onClick={() => setView("cancel")}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </>
            )}

            {view === "cancel" && (
              <>
                <SheetHeader>
                  <SheetTitle>Cancel Appointment</SheetTitle>
                  <SheetDescription>
                    {appointment.customerName} · {appointment.service}
                  </SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setView("view")}
                    className="mb-2 -ml-2"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back
                  </Button>

                  <div className="space-y-2">
                    <Label>Reason for cancellation (optional)</Label>
                    <Textarea
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      placeholder="Let the client know why..."
                      rows={3}
                    />
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="destructive"
                      className="flex-1"
                      onClick={handleCancel}
                      disabled={isLoading}
                    >
                      {isLoading ? "Cancelling..." : "Confirm Cancellation"}
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setView("view")}
                      disabled={isLoading}
                    >
                      Keep Appointment
                    </Button>
                  </div>
                </div>
              </>
            )}

            {view === "reschedule" && (
              <>
                <SheetHeader>
                  <SheetTitle>Reschedule Appointment</SheetTitle>
                  <SheetDescription>
                    {appointment.customerName} · {appointment.service}
                  </SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setView("view"); setNewDateTime(null); }}
                    className="mb-2 -ml-2"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back
                  </Button>

                  <DateTimeSelectionStep
                    businessId={businessId}
                    serviceId={appointment.serviceId}
                    providerId={appointment.providerId}
                    serviceDuration={appointment.duration}
                    selectedDateTime={newDateTime}
                    onSelectDateTime={(val) => setNewDateTime(val)}
                  />

                  {newDateTime && (
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-sm font-medium">New time:</p>
                      <p className="text-sm">
                        {newDateTime.date.toLocaleDateString("en-US", {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                        })}{" "}
                        at {newDateTime.time}
                      </p>
                    </div>
                  )}

                  <div className="flex gap-2 pt-2">
                    <Button
                      className="flex-1 bg-figgy-500 hover:bg-figgy-600"
                      onClick={handleReschedule}
                      disabled={!newDateTime || isLoading}
                    >
                      {isLoading ? "Rescheduling..." : "Confirm Reschedule"}
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => { setView("view"); setNewDateTime(null); }}
                      disabled={isLoading}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
